/**
 * Unified clinician Inbox — the single home for all four overlapping
 * "incoming work" surfaces that the June 2026 technical audit called out
 * (Issue 17). The four standalone pages still exist for deep-link
 * compatibility, but the menu now points here and presents them as tabs:
 *
 *   • Care Requests — open patient-posted needs to bid on
 *   • Case Requests — direct AI-matched patients awaiting your response
 *   • Offers       — outgoing offers awaiting patient response
 *   • Engagements  — long-term agency staffing
 *
 * Each tab renders the existing list/empty-state in this page so the
 * clinician never loses the sidebar (also addresses Issue 15).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems } from '../components/brand/navItems';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Inbox, Megaphone, Users, Handshake, Building2, ArrowRight, MapPin, CheckCircle,
  XCircle, Clock, Loader2, Sparkles,
} from 'lucide-react';
import { ClinicianEngagementsInbox } from '../components/brand/AgencyCards';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'cases', label: 'Case Requests', icon: Users,
    hint: 'AI-matched families directly inviting you' },
  { id: 'requests', label: 'Care Requests', icon: Megaphone,
    hint: 'Open patient-posted needs you can bid on' },
  { id: 'offers', label: 'Offers', icon: Handshake,
    hint: 'Outgoing offers awaiting patient response' },
  { id: 'engagements', label: 'Engagements', icon: Building2,
    hint: 'Long-term agency staffing assignments' },
];

export default function ClinicianInbox() {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('cases');
  const [matches, setMatches] = useState([]);
  const [careRequests, setCareRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Allow ?tab=offers etc. to deep-link straight into a tab.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    if (t && TABS.find((x) => x.id === t)) setTab(t);
  }, [location.search]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [matchesRes, requestsRes, offersRes] = await Promise.all([
        axios.get(`${API}/clinician/matches`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/care-requests/open`, getAuthHeader()).catch(() => ({ data: [] })),
        axios.get(`${API}/offers`, getAuthHeader()).catch(() => ({ data: [] })),
      ]);
      setMatches(matchesRes.data || []);
      // /api/care-requests/open returns {care_requests: [...]} (wrapper object),
      // not a plain array — unwrap defensively so the Care Requests tab
      // doesn't crash with `requests.slice is not a function`.
      const rawRequests = requestsRes.data;
      setCareRequests(
        Array.isArray(rawRequests) ? rawRequests : (rawRequests?.care_requests || [])
      );
      setOffers(offersRes.data || []);
    } catch (e) {
      toast.error('Could not load inbox');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const pending = matches.filter((m) => m.status === 'pending');
  const counts = {
    cases: pending.length,
    requests: careRequests.length,
    offers: offers.filter((o) => ['pending', 'countered'].includes(o.status)).length,
    engagements: 0, // populated on-demand inside the engagements tab
  };

  const navItems = clinicianNavItems({ pathname: '/clinician/inbox', pendingMatchesCount: pending.length });

  const respondToMatch = async (matchId, status) => {
    try {
      await axios.put(`${API}/clinician/matches/${matchId}/respond`, { status }, getAuthHeader());
      toast.success(`Match ${status}!`);
      fetchAll();
    } catch (e) {
      toast.error('Could not update match');
    }
  };

  const setTabAndUrl = (id) => {
    setTab(id);
    const params = new URLSearchParams(location.search);
    params.set('tab', id);
    navigate(`/clinician/inbox?${params.toString()}`, { replace: true });
  };

  return (
    <DashboardShell
      navItems={navItems}
      title="Inbox"
      subtitle="One place for every incoming opportunity — Care Requests, Case Requests, Offers, and Engagements."
      testId="clinician-inbox"
    >
      {/* Tab bar */}
      <div className="cn-card p-2 mb-6 flex flex-wrap gap-1.5" data-testid="inbox-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          const count = counts[t.id];
          return (
            <button
              key={t.id}
              onClick={() => setTabAndUrl(t.id)}
              data-testid={`inbox-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold transition-all ${
                active
                  ? 'bg-cn-primary text-white shadow-md shadow-cn-primary/20'
                  : 'text-cn-text-muted hover:bg-cn-bone-100 hover:text-cn-text'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
              {typeof count === 'number' && count > 0 && (
                <Badge className={active ? 'bg-white/25 text-white border-0' : 'bg-cn-accent/15 text-cn-accent border-0'} data-testid={`inbox-tab-count-${t.id}`}>
                  {count}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-sm text-cn-text-muted mb-5">
        {TABS.find((t) => t.id === tab)?.hint}
      </p>

      {loading ? (
        <div className="cn-card p-16 text-center"><Loader2 className="w-7 h-7 text-cn-primary animate-spin mx-auto" /></div>
      ) : (
        <>
          {tab === 'cases' && (
            <CaseRequestsTab pending={pending} onRespond={respondToMatch} />
          )}
          {tab === 'requests' && (
            <CareRequestsTab requests={careRequests} />
          )}
          {tab === 'offers' && (
            <OffersTab offers={offers} />
          )}
          {tab === 'engagements' && (
            <div className="cn-card p-6 sm:p-7" data-testid="inbox-tab-content-engagements">
              <ClinicianEngagementsInbox getAuthHeader={getAuthHeader} />
            </div>
          )}
        </>
      )}
    </DashboardShell>
  );
}

function CaseRequestsTab({ pending, onRespond }) {
  if (pending.length === 0) {
    return (
      <div className="cn-card p-12 text-center" data-testid="inbox-tab-content-cases-empty">
        <Inbox className="w-10 h-10 text-cn-text-muted mx-auto mb-3" />
        <h3 className="font-display text-lg font-semibold mb-1">No pending case requests</h3>
        <p className="text-sm text-cn-text-muted">When CaraLoom AI matches a family to your practice, they land here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="inbox-tab-content-cases">
      {pending.map((m, i) => (
        <div key={m.id} className="cn-card p-5 hover:border-cn-primary/40 transition-colors" data-testid={`inbox-case-${i}`}>
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-11 h-11 rounded-2xl bg-cn-accent/15 text-cn-accent flex items-center justify-center font-bold">
                  {m.patient?.first_name?.[0]}{m.patient?.last_name?.[0]}
                </div>
                <div>
                  <h4 className="font-semibold text-cn-text">{m.patient?.first_name} {m.patient?.last_name}</h4>
                  <p className="text-xs text-cn-text-muted flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {m.patient_profile?.city || 'Location on file'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                <Tile label="Care level" value={m.patient_profile?.recommended_care_level || 'N/A'} />
                <Tile label="Complexity" value={`${m.patient_profile?.care_complexity_score || '--'}/100`} />
                <Tile label="Urgency" value={m.patient_profile?.urgency_level || 'N/A'} />
                <Tile label="Match score" value={`${m.match_score}/100`} />
              </div>
            </div>
            <div className="flex lg:flex-col gap-2 lg:border-l lg:border-cn-border/50 lg:pl-5">
              <Button onClick={() => onRespond(m.id, 'accepted')} className="cn-btn cn-btn-primary !py-2 !px-4" data-testid={`inbox-accept-${i}`}>
                <CheckCircle className="w-4 h-4" /> Accept
              </Button>
              <Button onClick={() => onRespond(m.id, 'declined')} className="cn-btn cn-btn-ghost !py-2 !px-4 !text-cn-error hover:!bg-cn-error/10" data-testid={`inbox-decline-${i}`}>
                <XCircle className="w-4 h-4" /> Decline
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function CareRequestsTab({ requests }) {
  if (requests.length === 0) {
    return (
      <div className="cn-card p-12 text-center" data-testid="inbox-tab-content-requests-empty">
        <Megaphone className="w-10 h-10 text-cn-text-muted mx-auto mb-3" />
        <h3 className="font-display text-lg font-semibold mb-1">No open care requests right now</h3>
        <p className="text-sm text-cn-text-muted">Families post needs here for clinicians like you to bid on. Check back soon.</p>
        <Link to="/clinician/care-requests">
          <Button className="cn-btn cn-btn-primary mt-4" data-testid="inbox-requests-browse-btn">
            Browse full marketplace <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="inbox-tab-content-requests">
      {requests.slice(0, 8).map((r, i) => (
        <Link key={r.id} to={`/clinician/care-requests`} className="cn-card p-5 hover:border-cn-primary/40 transition-colors block" data-testid={`inbox-request-${i}`}>
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-cn-text-muted font-semibold">{r.service_type}</p>
              <h4 className="font-display font-semibold text-base mt-0.5 text-cn-text">{r.title || 'Care request'}</h4>
              <p className="text-xs text-cn-text-muted mt-1">{r.city || 'Location on file'}</p>
            </div>
            {r.budget_rate && <Badge className="bg-cn-accent/15 text-cn-accent">${r.budget_rate}/hr</Badge>}
          </div>
          <p className="text-sm text-cn-text mt-3 line-clamp-3">{r.description || r.summary || ''}</p>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-cn-border/60">
            <span className="text-xs text-cn-text-muted inline-flex items-center gap-1"><Clock className="w-3 h-3" />Posted {r.created_at ? new Date(r.created_at).toLocaleDateString() : 'recently'}</span>
            <span className="text-xs font-semibold text-cn-primary inline-flex items-center gap-1">View &rarr;</span>
          </div>
        </Link>
      ))}
      <Link to="/clinician/care-requests" className="cn-card p-5 border-dashed flex items-center justify-center text-sm font-semibold text-cn-primary hover:bg-cn-primary/5 transition-colors" data-testid="inbox-requests-see-all">
        See all open requests <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  );
}

function OffersTab({ offers }) {
  const open = offers.filter((o) => ['pending', 'countered', 'accepted'].includes(o.status));
  if (open.length === 0) {
    return (
      <div className="cn-card p-12 text-center" data-testid="inbox-tab-content-offers-empty">
        <Handshake className="w-10 h-10 text-cn-text-muted mx-auto mb-3" />
        <h3 className="font-display text-lg font-semibold mb-1">No active offers</h3>
        <p className="text-sm text-cn-text-muted">When a family sends you an offer, you can accept, counter, or decline it here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="inbox-tab-content-offers">
      {open.map((o, i) => (
        <Link key={o.id} to="/clinician/offers" className="cn-card p-5 hover:border-cn-primary/40 transition-colors block" data-testid={`inbox-offer-${i}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-cn-text-muted font-semibold">From family</p>
              <h4 className="font-display font-semibold text-base mt-0.5 text-cn-text truncate">{o.patient_name || 'A CaraLoom family'}</h4>
              <p className="text-xs text-cn-text-muted mt-1">{o.service_type?.replace(/_/g, ' ')}</p>
            </div>
            <div className="text-right">
              <p className="font-display font-bold text-lg text-cn-primary">${o.current_rate}/hr</p>
              <p className="text-xs text-cn-text-muted">{o.duration_hours}h · ${o.total_amount}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-between pt-3 border-t border-cn-border/60">
            <Badge className={`${o.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-800'}`}>{o.status}</Badge>
            <span className="text-xs font-semibold text-cn-primary inline-flex items-center gap-1">Respond &rarr;</span>
          </div>
        </Link>
      ))}
      <Link to="/clinician/offers" className="cn-card p-4 border-dashed flex items-center justify-center text-sm font-semibold text-cn-primary hover:bg-cn-primary/5 transition-colors" data-testid="inbox-offers-see-all">
        See all offers & negotiation history <ArrowRight className="w-4 h-4 ml-2" />
      </Link>
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="px-3 py-2 rounded-xl bg-cn-bg/60 border border-cn-border/40">
      <p className="text-[10px] uppercase tracking-wider text-cn-text-muted font-bold">{label}</p>
      <p className="text-xs font-bold text-cn-text mt-0.5 truncate">{value}</p>
    </div>
  );
}
